/**
 * stores/auth
 * **/

import { defineStore } from 'pinia'
import { computed, reactive } from 'vue'
import { useRouter } from 'vue-router'
import { authFetch } from '@/composables/utils'
import { clearUserStorage, STORAGE_KEY_TOKENS } from '@/lib/storage'
import { consumeIntendedUrl, clearIntendedUrl } from '@/lib/redirectAfterLogin'
import { useQueryChatStore } from '@/stores/queryChat'
import { useCourseUploadStore } from '@/stores/courseUpload'

export interface User {
  id: number
  username: string
  email: string
  isSuperuser: boolean
  isStaff: boolean
  permissions: string[]
}

export interface Tokens {
  access: string
  refresh: string
}

interface AuthState {
  user: User | null
  tokens: Tokens | null
  isAuthenticated: boolean
}

function getUserIdFromToken(token: string): string | null {
  try {
    const base64Url = token.split('.')[1]
    // JWTs use base64url (RFC 4648 §5): `-` and `_` replace `+` and `/`.
    // atob() requires standard base64, so convert before decoding.
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const payload = JSON.parse(atob(padded))
    return String(payload.user_id ?? payload.sub ?? '')
  } catch {
    return null
  }
}

const performRequest = async (url: string, body: object) => {
  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      // 'X-API-KEY': import.meta.env.VITE_API_KEY,
    },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(errorData.detail || 'La richiesta è fallita')
  }
  return res.json() as Promise<Tokens>
}

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()
  const baseUrl = import.meta.env.VITE_API_URL

  const state: AuthState = reactive({
    user: null,
    tokens: null,
    isAuthenticated: false,
  })

  // Timer reference for silent refresh
  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  // Shared promise so concurrent callers (router guard + authFetch 401 handler)
  // always await the same in-flight refresh rather than issuing two requests.
  let activeRefreshPromise: Promise<void> | null = null

  const permissions = computed(() => state.user?.permissions || [])

  function hasPermission(permission: string): boolean {
    return permissions.value.includes(permission)
  }

  /**
   * Start a timer to refresh the token automatically before it expires.
   * Access token lifetime is 15 minutes, so we refresh at 14 minutes.
   */
  function startRefreshTimer() {
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = setTimeout(
      async () => {
        try {
          await refreshToken()
        } catch (e) {
          console.warn('Silent refresh failed, will retry on next request via interceptor.', e)
        }
      },
      14 * 60 * 1000,
    ) // 14 minutes
  }

  function stopRefreshTimer() {
    if (refreshTimer) {
      clearTimeout(refreshTimer)
      refreshTimer = null
    }
  }

  async function _updateStateWithTokens(tokens: Tokens) {
    state.tokens = tokens
    localStorage.setItem(STORAGE_KEY_TOKENS, JSON.stringify(tokens))
    startRefreshTimer() // Restart the timer
    await fetchSession()
  }

  async function fetchSession() {
    try {
      const session = await authFetch('/api/auth/session/')
      if (session && (session as { isAuthenticated: boolean }).isAuthenticated) {
        state.user = {
          id: (session as { id: number }).id,
          username: (session as { username: string }).username,
          email: (session as { email: string }).email,
          isSuperuser: (session as { is_superuser: boolean }).is_superuser,
          isStaff: (session as { is_staff: boolean }).is_staff,
          permissions: (session as { permissions: string[] }).permissions,
        }
        state.isAuthenticated = true
      } else {
        logout()
      }
    } catch (error) {
      console.error('Session fetch failed:', error)
      logout()
    }
  }

  async function restoreSession(): Promise<void> {
    const tokensJson = localStorage.getItem(STORAGE_KEY_TOKENS)
    if (tokensJson) {
      try {
        const tokens: Tokens = JSON.parse(tokensJson)
        state.tokens = tokens
        startRefreshTimer() // Start timer on restore
        await fetchSession()
      } catch {
        logout()
      }
    }
  }

  async function login(username: string, password: string): Promise<void> {
    const tokens = await performRequest(`${baseUrl}/auth/login/`, { username, password })
    await _updateStateWithTokens(tokens)
    const intendedUrl = consumeIntendedUrl()
    router.push(intendedUrl ?? '/')
  }

  /**
   * Log in using a pre-issued JWT pair (e.g. redeemed from an external-access token).
   * Stores the tokens and fetches the session exactly like a normal login.
   */
  async function loginWithTokens(tokens: Tokens): Promise<void> {
    await _updateStateWithTokens(tokens)
    clearIntendedUrl()
    router.push('/')
  }

  async function refreshToken(): Promise<void> {
    if (activeRefreshPromise) return activeRefreshPromise

    activeRefreshPromise = (async () => {
      if (!state.tokens?.refresh) {
        logout()
        throw new Error('Nessun refresh token disponibile.')
      }
      try {
        const tokens = await performRequest(`${baseUrl}/auth/refresh/`, {
          refresh: state.tokens.refresh,
        })
        await _updateStateWithTokens(tokens)
      } catch (error) {
        console.error('Refresh fallito:', error)
        logout()
        throw error
      }
    })().finally(() => {
      activeRefreshPromise = null
    })

    return activeRefreshPromise
  }

  function logout(): void {
    stopRefreshTimer()

    // Reset in-memory state of stores that hold user-specific data.
    // Called lazily inside the function to avoid circular import issues.
    useQueryChatStore().clearPersistedState()
    useCourseUploadStore().clearAllData()

    // Wipe all user-specific localStorage entries in one pass
    // (tokens, chat history, course draft, all filter keys).
    clearUserStorage()
    clearIntendedUrl()

    state.user = null
    state.tokens = null
    state.isAuthenticated = false

    router.push('/login')
  }

  /**
   * Handles token changes written by other browser tabs.
   *
   * - Tokens cleared elsewhere → log out this tab too.
   * - Same user refreshed elsewhere → sync the new tokens into memory so this
   *   tab does not use a stale access token and trigger unnecessary 401s.
   * - Different user written elsewhere → force logout to prevent a silent
   *   user switch on the next page reload.
   */
  function onStorageChange(event: StorageEvent): void {
    if (event.key !== STORAGE_KEY_TOKENS) return

    if (!event.newValue) {
      if (state.isAuthenticated) logout()
      return
    }

    if (!state.isAuthenticated) return

    try {
      const incoming: Tokens = JSON.parse(event.newValue)
      const incomingId = getUserIdFromToken(incoming.access)
      const currentId = getUserIdFromToken(state.tokens?.access ?? '')

      if (incomingId && currentId && incomingId !== currentId) {
        // Another tab logged in as a different user — prevent silent switch.
        logout()
      } else {
        // Same user's tokens were refreshed in another tab — keep in-memory
        // tokens current so our next request uses the fresh access token.
        state.tokens = incoming
      }
    } catch {
      logout()
    }
  }

  // Sync auth state across browser tabs via the storage event.
  // The store is a Pinia singleton, so this listener is registered exactly once.
  window.addEventListener('storage', onStorageChange)

  return {
    user: computed(() => state.user),
    isAuthenticated: computed(() => state.isAuthenticated),
    restoreSession,
    login,
    loginWithTokens,
    refreshToken,
    logout,
    hasPermission,
  }
})
